import { useState, useEffect } from "react"
import { cn, createUuid } from "@/lib/utils"
import { Icon } from "@/icons/icon"
import Select, { SelectOption } from "@/components/select"

export interface LeakageEntry {
  id: string
  count: number
  type: string
}

export interface LeakageWidgetData {
  entries: LeakageEntry[]
  remark?: string
}

interface LeakageWidgetProps {
  value: LeakageWidgetData
  onChange: (data: LeakageWidgetData) => void
  leakageTypes: SelectOption[]
  totalChecked: number
  totalLeaked: number
  className?: string
  disabled?: boolean
  isSubmitted?: boolean
  onClickErrorFlag?: () => void
  paramRemark?: string
}

export function LeakageWidget({
  value,
  onChange,
  leakageTypes,
  totalChecked,
  totalLeaked,
  className,
  disabled = false,
  isSubmitted = false,
  onClickErrorFlag,
  paramRemark,
}: LeakageWidgetProps) {
  const [entries, setEntries] = useState<LeakageEntry[]>(value?.entries || [])

  useEffect(() => {
    if (value?.entries) {
      setEntries(value.entries)
    }
  }, [value])

  // Calculate leakage percentage
  const checkedNum = Number(totalChecked) || 0
  const leakedNum = Number(totalLeaked) || 0
  const leakagePercentage =
    checkedNum > 0 ? ((leakedNum / checkedNum) * 100).toFixed(2) : "0"

  const isOverThreshold = Number(leakagePercentage) > 25

  // Validate sum of entries equals total leaked
  const entriesSum = entries.reduce((sum, entry) => sum + (entry.count || 0), 0)
  const isValid = entriesSum === totalLeaked

  function addEntry() {
    const newEntry: LeakageEntry = {
      id: createUuid(),
      count: 0,
      type: "",
    }
    const newEntries = [...entries, newEntry]
    setEntries(newEntries)
    onChange({ entries: newEntries, remark: value?.remark })
  }

  function removeEntry(id: string) {
    const newEntries = entries.filter((entry) => entry.id !== id)
    setEntries(newEntries)
    onChange({ entries: newEntries, remark: value?.remark })
  }

  function updateEntry(id: string, updates: Partial<LeakageEntry>) {
    // Calculate current sum of other entries (excluding the one being updated)
    const otherEntriesSum = entries
      .filter((entry) => entry.id !== id)
      .reduce((sum, entry) => sum + (entry.count || 0), 0)

    // If updating count, cap it to not exceed totalLeaked
    if (updates.count !== undefined) {
      const remaining = Math.max(0, totalLeaked - otherEntriesSum)
      updates.count = Math.min(updates.count, remaining)
    }

    const newEntries = entries.map((entry) =>
      entry.id === id ? { ...entry, ...updates } : entry
    )
    setEntries(newEntries)
    onChange({ entries: newEntries, remark: value?.remark })
  }

  // Auto-add first entry when totalLeaked changes from 0 (only if sum is less than total)
  useEffect(() => {
    if (totalLeaked > 0 && entries.length === 0) {
      const newEntry: LeakageEntry = {
        id: crypto.randomUUID(),
        count: 0,
        type: "",
      }
      setEntries([newEntry])
      onChange({ entries: [newEntry], remark: value?.remark })
    }
  }, [totalLeaked])

 

  const showFlagButton = totalLeaked > 0 && onClickErrorFlag

  if (isSubmitted) {
    return (
      <div className={cn("space-y-3", className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-800">
            Leakage Breakdown
          </span>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-sm font-semibold px-3 py-1 rounded border border-dashed",
                isOverThreshold
                  ? "text-red-600 border-red-400"
                  : "text-slate-700 border-slate-400"
              )}
            >
              Leakage &nbsp; {leakagePercentage}%
            </span>
            {showFlagButton && (
              <button
                type="button"
                onClick={onClickErrorFlag}
                className="hover:opacity-70 transition-opacity"
              >
                <Icon
                  name={paramRemark ? "RedFlag" : "FlagNeutral"}
                  className="h-4 w-4"
                />
              </button>
            )}
          </div>
        </div>

        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
          {entries.map((entry) => (
            <div key={entry.id} className="flex items-center gap-3 text-sm">
              <span className="inline-flex min-w-6 justify-center rounded-full bg-white px-2 py-1 font-semibold text-slate-800">
                {entry.count}
              </span>
              <span className="text-slate-700">{entry.type}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between gap-3 pt-2">
        <div className="flex items-center gap-2 text-sm text-slate-600">
        <span className="font-medium">Leaked Packets</span>
        <span className="text-slate-400">|</span>
        <span className="font-medium">Leakage Type</span>
        </div>
        {showFlagButton && (
          <button
            type="button"
            onClick={onClickErrorFlag}
            className="hover:opacity-70 transition-opacity"
          >
            <Icon
              name={paramRemark ? "RedFlag" : "FlagNeutral"}
              className="h-4 w-4"
            />
          </button>
        )}
      </div>

      <div className="space-y-2">
        {entries.map((entry, index) => (
          <div
            key={entry.id}
            className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 overflow-hidden"
          >
            {/* Count input */}
            <input
              type="number"
              value={entry.count || ""}
              disabled={disabled}
              onChange={(e) =>
                updateEntry(entry.id, { count: Number(e.target.value) || 0 })
              }
              className="w-16 h-12 px-3 text-center border-r border-slate-200 text-sm focus:outline-none disabled:bg-slate-50"
              placeholder="0"
            />

            {/* Type dropdown */}
            <Select
              options={leakageTypes}
              value={entry.type}
              onChange={(type) => updateEntry(entry.id, { type })}
              disabled={disabled}
              placeholder="Select leakage type"
              className="flex-1"
              selectClassName="border-none h-12 rounded-none"
            />

            {/* Remove/Add button */}
            <button
              type="button"
              disabled={disabled || (index === entries.length - 1 && entriesSum >= totalLeaked)}
              onClick={() => {
                if (index === entries.length - 1) {
                  addEntry()
                } else {
                  removeEntry(entry.id)
                }
              }}
              className={cn(
                "w-12 h-12 flex items-center justify-center transition-colors",
                index === entries.length - 1
                  ? entriesSum >= totalLeaked
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-blue-500 hover:bg-blue-50"
                  : "text-slate-400 hover:bg-slate-50 hover:text-red-500"
              )}
            >
              <Icon
                name={index === entries.length - 1 ? "Plus" : "Dash"}
                className="h-5 w-5"
              />
            </button>
          </div>
        ))}
      </div>

      {!isValid && totalLeaked > 0 && (
        <div className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">
          Sum of breakdown ({entriesSum}) must equal Total Leaked ({totalLeaked})
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <span className="text-sm text-slate-600">Leakage:</span>
        <span
          className={cn(
            "text-sm font-semibold",
            isOverThreshold ? "text-red-600" : "text-green-600"
          )}
        >
          {leakagePercentage}%
        </span>
      </div>
    </div>
  )
}

export default LeakageWidget
